// CoverageValue — renders a single figure's coverage resolution honestly.
//
// Load-bearing: cold_armed and pending BOTH render a DASH with DIFFERENT
// captions; live_zero is the ONLY 0 (green dot); absent => "not available";
// unknown/malformed => neutral dash. Never fabricates a number. No card layout.
import React from "react";

import type { Denominator } from "../../../chatBot/models/machineEvidenceModels";
import type { CoverageResolution } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { EM_DASH, LIVE_ZERO_DOT, PLACEHOLDER_FG } from "./honestyTokens";

export interface CoverageValueProps {
  resolution: CoverageResolution;
  unit?: string | null;
  denominator?: Denominator | null;
  /** dim the value when the card feed is DARK. */
  dim?: boolean;
}

const fmt = (v: number | string | null): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return v.toLocaleString();
  return v;
};

export const CoverageValue: React.FC<CoverageValueProps> = ({
  resolution,
  unit,
  denominator,
  dim,
}) => {
  const { translate } = useTranslation("machineEvidence");
  const opacity = dim ? 0.55 : 1;

  // i18n caption: the client base label lives under `captionKey`; the payload
  // reason (`captionReason`, e.g. breakdown.pending_reason) is appended VERBATIM
  // after the translated base — bot data is never translated (no double-translate).
  const captionText: string | null = resolution.showsValue
    ? null
    : (() => {
        const base = resolution.captionKey
          ? (translate(resolution.captionKey, {
              defaultValue: resolution.captionBase ?? resolution.caption ?? "",
            }) as string)
          : resolution.caption ?? "";
        return resolution.captionReason ? `${base} (${resolution.captionReason})` : base;
      })();

  if (resolution.showsValue) {
    const isZeroDot = resolution.kind === "live_zero";
    return (
      <span
        className="msc-coverage-value"
        style={{ display: "inline-flex", alignItems: "baseline", gap: 6, opacity }}
      >
        {isZeroDot && (
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: LIVE_ZERO_DOT,
              alignSelf: "center",
            }}
          />
        )}
        <span style={{ fontFamily: dt.fontMono, color: dt.textPrimary, fontWeight: 600 }}>
          {fmt(resolution.value)}
          {unit ? <span style={{ marginLeft: 2 }}>{unit}</span> : null}
        </span>
        {denominator ? (
          <span style={{ color: dt.textSecondary, fontSize: "0.85em", fontFamily: dt.fontMono }}>
            · {denominator.num.toLocaleString()} / {denominator.den.toLocaleString()}
          </span>
        ) : null}
      </span>
    );
  }

  // Placeholder kinds: absent renders its caption inline; the rest render a
  // dash glyph followed by the distinguishing caption.
  const showDash = resolution.kind !== "absent";
  return (
    <span
      className={`msc-coverage-placeholder msc-coverage-${resolution.kind}`}
      style={{ display: "inline-flex", alignItems: "baseline", gap: 6, color: PLACEHOLDER_FG, opacity }}
      data-coverage-kind={resolution.kind}
    >
      {showDash && (
        <span aria-hidden style={{ fontFamily: dt.fontMono }}>
          {EM_DASH}
        </span>
      )}
      {captionText ? (
        <span style={{ fontSize: "0.85em", fontStyle: "italic" }}>{captionText}</span>
      ) : null}
    </span>
  );
};

export default CoverageValue;
